import React, { useEffect, useRef, useState } from 'react';
import AppModal from '../../shared/AppModal';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import Swal from 'sweetalert2';
import axios from '../../utils/axios';
import {
    helpDeskTicketDetail,
    helpDeskTicketComments,
    helpDeskTicketAttachments,
    helpDeskAttachmentDetail,
    helpDeskTicketActivity,
} from '../../utils/urls';
import {
    STATUS_OPTIONS,
    PRIORITY_OPTIONS,
    CATEGORY_OPTIONS,
    SOURCE_LABELS,
    formatDateTime,
    formatFileSize,
    resolveUserName,
} from '../../pages/helpDesk/helpDeskConstants';
import '../../pages/helpDesk/helpDesk.css';

const spinner = (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
        <ProgressSpinner style={{ width: 32, height: 32 }} strokeWidth="5" animationDuration=".9s" />
    </div>
);

export default function TicketDetailDialog({ visible, ticketId, onHide, usersList, currentUserId, isSuperuser, toast, onUpdated }) {
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(false);
    const [forbidden, setForbidden] = useState(false);

    const [comments, setComments] = useState([]);
    const [commentsNext, setCommentsNext] = useState(null);
    const [commentsPage, setCommentsPage] = useState(1);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [postingComment, setPostingComment] = useState(false);

    const [attachments, setAttachments] = useState([]);
    const [attachmentsNext, setAttachmentsNext] = useState(null);
    const [attachmentsPage, setAttachmentsPage] = useState(1);
    const [attachmentsLoading, setAttachmentsLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');

    const [activity, setActivity] = useState([]);
    const [activityLoading, setActivityLoading] = useState(false);

    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!visible || !ticketId) return;
        setForbidden(false);
        fetchTicket();
        fetchComments(1);
        fetchAttachments(1);
        fetchActivity();
    }, [visible, ticketId]);

    const fetchTicket = () => {
        setLoading(true);
        axios.get(helpDeskTicketDetail(ticketId))
            .then(res => setTicket(res.data))
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load ticket', life: 3000 }))
            .finally(() => setLoading(false));
    };

    const fetchComments = (page) => {
        setCommentsLoading(true);
        axios.get(helpDeskTicketComments(ticketId), { params: { page, page_size: 20 } })
            .then(res => {
                setComments(prev => page === 1 ? (res.data.results || []) : [...prev, ...(res.data.results || [])]);
                setCommentsNext(res.data.next || null);
                setCommentsPage(page);
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load comments', life: 3000 }))
            .finally(() => setCommentsLoading(false));
    };

    const fetchAttachments = (page) => {
        setAttachmentsLoading(true);
        axios.get(helpDeskTicketAttachments(ticketId), { params: { page, page_size: 20 } })
            .then(res => {
                setAttachments(prev => page === 1 ? (res.data.results || []) : [...prev, ...(res.data.results || [])]);
                setAttachmentsNext(res.data.next || null);
                setAttachmentsPage(page);
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load attachments', life: 3000 }))
            .finally(() => setAttachmentsLoading(false));
    };

    const fetchActivity = () => {
        setActivityLoading(true);
        axios.get(helpDeskTicketActivity(ticketId))
            .then(res => setActivity(Array.isArray(res.data) ? res.data : []))
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load activity', life: 3000 }))
            .finally(() => setActivityLoading(false));
    };

    const canEdit = !forbidden && !!ticket && (isSuperuser || ticket.assigned_to === currentUserId);

    const handlePatch = (field, value) => {
        const prevValue = ticket[field];
        setTicket(t => ({ ...t, [field]: value }));
        axios.patch(helpDeskTicketDetail(ticketId), { [field]: value })
            .then(res => {
                setTicket(res.data);
                toast.current?.show({ severity: 'success', summary: 'Updated', detail: `Ticket ${field.replace('_', ' ')} updated`, life: 2000 });
                onUpdated?.();
            })
            .catch(err => {
                setTicket(t => ({ ...t, [field]: prevValue }));
                if (err?.response?.status === 403) {
                    setForbidden(true);
                    toast.current?.show({ severity: 'error', summary: 'Not Allowed', detail: "You don't have permission to change this ticket", life: 4000 });
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to update ticket', life: 3000 });
                }
            });
    };

    const handlePostComment = () => {
        if (!newComment.trim()) return;
        setPostingComment(true);
        axios.post(helpDeskTicketComments(ticketId), { body: newComment.trim() })
            .then(res => {
                setComments(prev => [res.data, ...prev]);
                setNewComment('');
                fetchActivity();
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to post comment', life: 3000 }))
            .finally(() => setPostingComment(false));
    };

    const handleFileSelected = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append('file', file);
        fd.append('file_name', file.name);
        axios.post(helpDeskTicketAttachments(ticketId), fd, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(res => {
                setAttachments(prev => [res.data, ...prev]);
                fetchActivity();
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Upload failed', life: 3000 }))
            .finally(() => setUploading(false));
    };

    const startRename = (a) => { setRenamingId(a.id); setRenameValue(a.file_name); };
    const cancelRename = () => { setRenamingId(null); setRenameValue(''); };

    const submitRename = (a) => {
        if (!renameValue.trim()) return;
        axios.patch(helpDeskAttachmentDetail(a.id), { file_name: renameValue.trim() })
            .then(res => {
                setAttachments(prev => prev.map(x => x.id === a.id ? res.data : x));
                setRenamingId(null);
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Rename failed', life: 3000 }));
    };

    const handleDeleteAttachment = (a) => {
        Swal.fire({
            title: 'Delete Attachment?',
            text: `This will remove "${a.file_name}".`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it',
            confirmButtonColor: '#e2574a',
            background: '#13131a',
            color: '#e2e8f0',
        }).then(result => {
            if (!result.isConfirmed) return;
            axios.delete(helpDeskAttachmentDetail(a.id))
                .then(() => {
                    setAttachments(prev => prev.filter(x => x.id !== a.id));
                    fetchActivity();
                })
                .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Delete failed', life: 3000 }));
        });
    };

    return (
        <AppModal
            header={ticket ? `#${ticket.id} — ${ticket.title}` : 'Ticket'}
            visible={visible}
            onHide={onHide}
            panelStyle={{ width: 720 }}
        >
            {loading || !ticket ? spinner : (
                <div className="hd-detail">
                    {/* Overview */}
                    <div className="hd-detail-overview">
                        <div className="hd-detail-field">
                            <span className="hd-detail-label">Description</span>
                            <p className="hd-detail-desc">{ticket.description || '—'}</p>
                        </div>
                        <div className="hd-detail-grid">
                            <div className="hd-detail-field">
                                <span className="hd-detail-label">Source</span>
                                <span>{SOURCE_LABELS[ticket.source] || ticket.source || '—'}</span>
                            </div>
                            <div className="hd-detail-field">
                                <span className="hd-detail-label">Reported By</span>
                                <span>{resolveUserName(ticket.reported_by, usersList)}</span>
                            </div>
                            <div className="hd-detail-field">
                                <span className="hd-detail-label">Created</span>
                                <span>{formatDateTime(ticket.created_at)}</span>
                            </div>
                            <div className="hd-detail-field">
                                <span className="hd-detail-label">Updated</span>
                                <span>{formatDateTime(ticket.updated_at)}</span>
                            </div>
                            <div className="hd-detail-field">
                                <span className="hd-detail-label">Resolved</span>
                                <span>{formatDateTime(ticket.resolved_at)}</span>
                            </div>
                            <div className="hd-detail-field">
                                <span className="hd-detail-label">Closed</span>
                                <span>{formatDateTime(ticket.closed_at)}</span>
                            </div>
                        </div>
                    </div>

                    <Divider />

                    {/* Editable controls */}
                    <div className="hd-detail-section">
                        <div className="hd-section-title">Manage Ticket</div>
                        {!canEdit && (
                            <div className="hd-perm-note">Only admins or the assigned user can change these fields.</div>
                        )}
                        <div className="hd-detail-grid">
                            <div className="hd-detail-field">
                                <span className="hd-detail-label">Status</span>
                                <Dropdown
                                    value={ticket.status}
                                    options={STATUS_OPTIONS}
                                    onChange={e => handlePatch('status', e.value)}
                                    disabled={!canEdit}
                                    className="hd-input"
                                    panelClassName="hd-dd-panel"
                                />
                            </div>
                            <div className="hd-detail-field">
                                <span className="hd-detail-label">Priority</span>
                                <Dropdown
                                    value={ticket.priority}
                                    options={PRIORITY_OPTIONS}
                                    onChange={e => handlePatch('priority', e.value)}
                                    disabled={!canEdit}
                                    className="hd-input"
                                    panelClassName="hd-dd-panel"
                                />
                            </div>
                            <div className="hd-detail-field">
                                <span className="hd-detail-label">Category</span>
                                <Dropdown
                                    value={ticket.category}
                                    options={CATEGORY_OPTIONS}
                                    onChange={e => handlePatch('category', e.value)}
                                    disabled={!canEdit}
                                    className="hd-input"
                                    panelClassName="hd-dd-panel"
                                />
                            </div>
                            <div className="hd-detail-field">
                                <span className="hd-detail-label">Assigned To</span>
                                <Dropdown
                                    value={ticket.assigned_to}
                                    options={usersList}
                                    onChange={e => handlePatch('assigned_to', e.value)}
                                    disabled={!canEdit}
                                    filter
                                    showClear
                                    placeholder="Unassigned"
                                    className="hd-input"
                                    panelClassName="hd-dd-panel"
                                />
                            </div>
                        </div>
                    </div>

                    <Divider />

                    {/* Comments */}
                    <div className="hd-detail-section">
                        <div className="hd-section-title">Comments</div>
                        <div className="hd-comment-form">
                            <InputTextarea
                                value={newComment}
                                onChange={e => setNewComment(e.target.value)}
                                placeholder="Add a comment…"
                                rows={2}
                                className="hd-input"
                            />
                            <button
                                className="hd-btn hd-btn-primary hd-btn-sm"
                                onClick={handlePostComment}
                                disabled={!newComment.trim() || postingComment}
                            >
                                {postingComment ? <i className="pi pi-spin pi-spinner" /> : 'Post'}
                            </button>
                        </div>

                        {commentsLoading && commentsPage === 1 ? spinner : (
                            <div className="hd-comments">
                                {comments.length === 0 && <div className="hd-empty">No comments yet</div>}
                                {comments.map(c => (
                                    <div key={c.id} className="hd-comment">
                                        <div className="hd-comment-head">
                                            <b>{resolveUserName(c.author, usersList)}</b>
                                            <span className="hd-faint">{formatDateTime(c.created_at)}</span>
                                        </div>
                                        <div className="hd-comment-body">{c.body}</div>
                                    </div>
                                ))}
                                {commentsNext && (
                                    <button className="hd-btn hd-btn-ghost hd-btn-sm" onClick={() => fetchComments(commentsPage + 1)} disabled={commentsLoading}>
                                        {commentsLoading ? 'Loading…' : 'Load more'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <Divider />

                    {/* Attachments */}
                    <div className="hd-detail-section">
                        <div className="hd-section-title">Attachments</div>
                        <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelected} />
                        <button className="hd-btn hd-btn-ghost hd-btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                            {uploading ? <><i className="pi pi-spin pi-spinner" /> Uploading…</> : <><i className="pi pi-upload" /> Upload File</>}
                        </button>

                        {attachmentsLoading && attachmentsPage === 1 ? spinner : (
                            <div className="hd-attachments">
                                {attachments.length === 0 && <div className="hd-empty">No attachments</div>}
                                {attachments.map(a => (
                                    <div key={a.id} className="hd-attachment-row">
                                        {renamingId === a.id ? (
                                            <input
                                                className="hd-rename-input"
                                                value={renameValue}
                                                onChange={e => setRenameValue(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') submitRename(a); if (e.key === 'Escape') cancelRename(); }}
                                                autoFocus
                                            />
                                        ) : (
                                            <a href={a.file_url} target="_blank" rel="noreferrer" className="hd-attachment-name">
                                                <i className="pi pi-file" /> {a.file_name}
                                            </a>
                                        )}
                                        <span className="hd-faint">{formatFileSize(a.file_size)}</span>
                                        <span className="hd-faint">{a.uploaded_by?.name || '—'}</span>
                                        <span className="hd-faint">{formatDateTime(a.uploaded_at)}</span>
                                        <div className="hd-attachment-actions">
                                            {renamingId === a.id ? (
                                                <>
                                                    <button className="hd-icon-btn" title="Save" onClick={() => submitRename(a)}><i className="pi pi-check" /></button>
                                                    <button className="hd-icon-btn" title="Cancel" onClick={cancelRename}><i className="pi pi-times" /></button>
                                                </>
                                            ) : (
                                                <>
                                                    <button className="hd-icon-btn" title="Rename" onClick={() => startRename(a)}><i className="pi pi-pencil" /></button>
                                                    <button className="hd-icon-btn hd-icon-btn-danger" title="Delete" onClick={() => handleDeleteAttachment(a)}><i className="pi pi-trash" /></button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {attachmentsNext && (
                                    <button className="hd-btn hd-btn-ghost hd-btn-sm" onClick={() => fetchAttachments(attachmentsPage + 1)} disabled={attachmentsLoading}>
                                        {attachmentsLoading ? 'Loading…' : 'Load more'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <Divider />

                    {/* Activity */}
                    <div className="hd-detail-section">
                        <div className="hd-section-title">Activity</div>
                        {activityLoading ? spinner : (
                            <div className="hd-activity">
                                {activity.length === 0 && <div className="hd-empty">No activity recorded</div>}
                                {activity.map(a => (
                                    <div key={a.id} className="hd-activity-item">
                                        <div className="hd-activity-dot" />
                                        <div className="hd-activity-content">
                                            <div><b>{a.user?.name || 'System'}</b> {a.activity}</div>
                                            <div className="hd-faint">{formatDateTime(a.created_at)} · {a.source}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </AppModal>
    );
}
